import { Card, expandKnownForms, isRetired } from '@lingoping/core';
import { StorageManager } from '../storage/index.js';

/**
 * "I already know this word, stop showing it" on a card in a review. The card is kept with its
 * history but hidden for good (`retired_at`): never due, not in Up next, not in the Cards tab,
 * not in the counts. The word and all its forms join the known words, grouped under the card's
 * dictionary form like a deck decision, so the Known words tab shows one row for it.
 */

/** Every form that counts as known for a card: the forms it lists, or those worked out from it. */
export function knownFormsForCard(card: Card): string[] {
  const lemma = (card.lemma ?? card.front_text).trim();
  const meta = card.linguistic_meta;
  const listed = meta?.forms?.length
    ? [lemma, ...meta.forms]
    : expandKnownForms({
        lemma,
        part_of_speech: card.part_of_speech ?? meta?.part_of_speech,
        linguistic_meta: { plural: meta?.plural, conjugation: meta?.conjugation },
      });
  const seen = new Set<string>();
  return listed.filter((form) => {
    const key = form.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export interface RetireResult {
  /** The card as it is stored now. */
  card: Card;
  /** The known-words group this created; false when the word was known already. */
  addedKnown: boolean;
}

const sameWord = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Whether the card is ONE of several meanings of its word (it has a hint, or the learner has
 * another card of the same spelling). Knowing "Bank (Möbel)" must not hide "Bank (Geld)", so for
 * such a card only the card itself is put away: the spelling does not join the known words, which
 * are kept per spelling and would cover every meaning.
 */
async function isOneOfSeveralMeanings(card: Card): Promise<boolean> {
  if (card.sense_hint || card.sense_key) return true;
  const word = (card.lemma ?? card.front_text).trim().toLowerCase();
  return (await StorageManager.getCards()).some(
    (other) =>
      other.id !== card.id &&
      (other.target_language || 'de') === (card.target_language || 'de') &&
      (other.lemma ?? other.front_text).trim().toLowerCase() === word
  );
}

export async function retireCard(card: Card, language = 'de'): Promise<RetireResult> {
  const base = (card.lemma ?? card.front_text).trim();
  if (await isOneOfSeveralMeanings(card)) {
    const at = new Date().toISOString();
    const put = await StorageManager.setCardRetired(card.id, at);
    return { card: put ?? { ...card, retired_at: at }, addedKnown: false };
  }
  const wasKnown = (await StorageManager.getKnownWords()).some((w) =>
    sameWord(w.base_lemma ?? w.lemma, base)
  );
  await StorageManager.addKnownForms(base, knownFormsForCard(card), language);
  // Only the retirement is changed, on the card as it is saved now: a picture or translation
  // that arrived meanwhile is kept.
  const retiredAt = new Date().toISOString();
  const retired = await StorageManager.setCardRetired(card.id, retiredAt);
  return { card: retired ?? { ...card, retired_at: retiredAt }, addedKnown: !wasKnown };
}

/** Takes the card back: it is shown again, and the known words this created go away. */
export async function unretireCard(
  card: Card,
  options: { removeKnown?: boolean } = {}
): Promise<Card> {
  const back = await StorageManager.setCardRetired(card.id, null);
  if (options.removeKnown) {
    await StorageManager.removeKnownGroup((card.lemma ?? card.front_text).trim());
  }
  return back ?? { ...card, retired_at: null };
}

/**
 * The learner removed a word from their known words: a card they retired for it is shown again
 * (the Known words tab is the one place that says what is known, so it also undoes this).
 */
export async function unretireCardsFor(baseLemma: string): Promise<number> {
  let count = 0;
  for (const card of await StorageManager.getCards()) {
    if (isRetired(card) && sameWord(card.lemma ?? card.front_text, baseLemma)) {
      await unretireCard(card);
      count++;
    }
  }
  return count;
}
